import { type ReactNode } from 'react';

import { cn } from '@/lib/utils';

import { Blueprint } from './Blueprint';

/**
 * Moldura de espécime do Design System (ADR-0065): a imagem de assinatura vive
 * dentro de um objeto de blueprint quadrado, com tratamento duotone no aço,
 * legenda mono e barra de escala de 10 mm no rodapé.
 *
 * A escala não é enfeite — é o que dá dimensão real ao traço numa peça que vai
 * para os autos. A conversão mm→px é do consumidor (depende do DPI com que o
 * espécime foi digitalizado): `scalePx` é quantos pixels de tela valem 10 mm
 * naquela imagem.
 *
 * O duotone e as camadas sobre a imagem exigem recorte (`overflow-hidden`), e
 * ele fica num filho para alcançar só a chapa: a legenda e a moldura da figura
 * ficam fora do recorte.
 */

export interface SpecimenFrameProps {
  src: string;
  /** Texto alternativo da imagem — i18n fica com o consumidor. */
  alt: string;
  /** Legenda mono no rodapé (origem, folha, data). */
  caption?: ReactNode;
  /** Rótulo da barra de escala, ex.: "10 mm". Omitir esconde a barra. */
  scaleLabel?: string;
  /** Largura em px que representa a escala na imagem exibida. Default 40. */
  scalePx?: number;
  /** Tratamento duotone em aço. Default `true`. */
  duotone?: boolean;
  className?: string;
  /** Camadas sobre a imagem (anotações, marcações do laudo). */
  children?: ReactNode;
}

export function SpecimenFrame({
  src,
  alt,
  caption,
  scaleLabel,
  scalePx = 40,
  duotone = true,
  className,
  children,
}: SpecimenFrameProps) {
  return (
    <Blueprint as="figure" className={cn('bg-paper', className)}>
      <div className="relative overflow-hidden" data-specimen-plate>
        <img src={src} alt={alt} className="block w-full" />
        {duotone ? (
          <span
            aria-hidden
            data-duotone
            className="pointer-events-none absolute inset-0 bg-accent mix-blend-color"
          />
        ) : null}
        {children}
      </div>
      {caption || scaleLabel ? (
        <figcaption className="flex items-end justify-between gap-sc-3 border-t border-border px-sc-3 py-sc-2">
          {caption ? (
            <span className="font-mono text-[11px] leading-tight text-fg-dim">{caption}</span>
          ) : (
            <span />
          )}
          {scaleLabel ? (
            <span className="flex flex-col items-center gap-1" data-specimen-scale>
              <span
                aria-hidden
                className="block h-[6px] border-x border-b border-fg/55"
                style={{ width: `${scalePx}px` }}
              />
              <span className="font-mono text-[10px] leading-none text-fg-dim">{scaleLabel}</span>
            </span>
          ) : null}
        </figcaption>
      ) : null}
    </Blueprint>
  );
}
